import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Auto-registers the current login as a tracked session and keeps it alive
 * with periodic heartbeats. This is what powers the Settings → Security
 * Sessions / Devices / Events panels.
 *
 * - Generates a stable per-browser device fingerprint (stored in localStorage)
 * - Detects browser, OS, device type from user-agent
 * - Resolves IP & geo from a public lookup (best-effort, fails silently)
 * - Calls security-manager.register_session on first mount per session
 * - Sends a heartbeat every 2 minutes to update last_activity_at
 */
export function useSessionTracker() {
  const { user, session } = useAuth();
  const sessionDbIdRef = useRef<string | null>(null);
  const registeredForTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !session) {
      sessionDbIdRef.current = null;
      registeredForTokenRef.current = null;
      return;
    }

    // Avoid double-registration for the same auth token
    const tokenKey = session.access_token.slice(-32);
    if (registeredForTokenRef.current === tokenKey) return;
    registeredForTokenRef.current = tokenKey;

    let cancelled = false;
    let heartbeatTimer: number | null = null;

    const run = async () => {
      try {
        // 1. Persistent device fingerprint
        let fingerprint = localStorage.getItem("vazoul-device-fp");
        if (!fingerprint) {
          fingerprint = crypto.randomUUID();
          localStorage.setItem("vazoul-device-fp", fingerprint);
        }

        // 2. Parse user-agent
        const ua = navigator.userAgent;
        const { browser, os, deviceType } = parseUA(ua);

        // 3. Best-effort IP + geo lookup
        let ip: string | null = null;
        let city: string | null = null;
        let country: string | null = null;
        try {
          const r = await fetch("https://ipapi.co/json/", { cache: "no-store" });
          if (r.ok) {
            const j = await r.json();
            ip = j.ip || null;
            city = j.city || null;
            country = j.country_code || j.country || null;
          }
        } catch { /* ignore — geo is optional */ }

        if (cancelled) return;

        // Re-fetch the freshest session token — the one from context may be
        // stale, and supabase.functions.invoke would otherwise fall back to
        // the anon publishable key (no `sub` claim → 401 from the function).
        const { data: fresh } = await supabase.auth.getSession();
        const accessToken = fresh?.session?.access_token;
        if (!accessToken) return;
        const authHeaders = { Authorization: `Bearer ${accessToken}` };

        // 4. Register the session (best-effort — ignore auth/race errors)
        const reg = await supabase.functions.invoke("security-manager", {
          headers: authHeaders,
          body: {
            action: "register_session",
            session_token: accessToken.slice(-64),
            device_fingerprint: fingerprint,
            device_name: `${browser} on ${os}`,
            device_type: deviceType,
            browser,
            os,
            ip_address: ip,
            city,
            country,
          },
        }).catch(() => ({ data: null, error: null as any }));

        if (cancelled) return;
        if (reg.error) {
          // Stale token / pre-login race — skip silently, heartbeat won't start.
          return;
        }
        sessionDbIdRef.current = reg.data?.session?.id || null;

        // 5. Seed/update trusted-device record (best-effort)
        await supabase.functions.invoke("security-manager", {
          headers: authHeaders,
          body: {
            action: "check_login_security",
            ip_address: ip,
            device_fingerprint: fingerprint,
            country,
            city,
            browser,
            os,
            device_type: deviceType,
            device_name: `${browser} on ${os}`,
          },
        }).catch(() => null);

        // 6. Heartbeat every 2 minutes
        const beat = async () => {
          if (cancelled || !sessionDbIdRef.current) return;
          try {
            const { data: cur } = await supabase.auth.getSession();
            const tok = cur?.session?.access_token;
            if (!tok) return;
            await supabase.functions.invoke("security-manager", {
              headers: { Authorization: `Bearer ${tok}` },
              body: { action: "heartbeat", session_id: sessionDbIdRef.current },
            });
          } catch { /* ignore */ }
        };
        heartbeatTimer = window.setInterval(beat, 2 * 60 * 1000);
      } catch (err) {
        console.warn("[SessionTracker] failed to register session", err);
      }
    };

    void run();

    return () => {
      cancelled = true;
      if (heartbeatTimer) window.clearInterval(heartbeatTimer);
    };
  }, [user, session]);
}

function parseUA(ua: string): { browser: string; os: string; deviceType: "desktop" | "mobile" | "tablet" | "unknown" } {
  let browser = "Unknown";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = "Safari";
  else if (/OPR\/|Opera/.test(ua)) browser = "Opera";

  let os = "Unknown";
  if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua)) os = "iOS";
  else if (/Linux/.test(ua)) os = "Linux";

  let deviceType: "desktop" | "mobile" | "tablet" | "unknown" = "desktop";
  if (/iPad|Tablet/i.test(ua)) deviceType = "tablet";
  else if (/Mobi|Android|iPhone|iPod/i.test(ua)) deviceType = "mobile";

  return { browser, os, deviceType };
}
